import { Box3, type Group, Vector3 } from 'three'
import { ThreeItemKeys } from '../../constants/three'

/**
 * Re-centers a group on the bounding box of its current (real) children, the
 * same way a freshly-imported group is centered (base-file-loader.ts). Needed
 * after removing a child: the group's own pivot otherwise stays wherever it
 * was when it still had its original children, so its position (used as-is
 * for a solo child's canvas selection/gizmo/RightBar position) drifts away
 * from where the remaining geometry actually is.
 *
 * Transient children (e.g. the Box3Helper selection outline) are excluded -
 * they aren't content and shouldn't skew the bounding box or be repositioned.
 */
export const recenterGroupOnChildren = (group: Group) => {
  const children = group.children.filter(child => !child.userData[ThreeItemKeys.TRANSIENT])
  if (children.length === 0) return

  group.updateMatrixWorld(true)

  const childWorldPositions = children.map(child => child.getWorldPosition(new Vector3()))
  const boundingBox = new Box3()
  children.forEach(child => boundingBox.union(new Box3().setFromObject(child)))
  const center = boundingBox.getCenter(new Vector3())

  group.position.copy(center)
  group.updateMatrixWorld(true)

  children.forEach((child, index) => {
    child.position.copy(group.worldToLocal(childWorldPositions[index]))
  })
}
